import type { FC, PropsWithChildren, ReactNode } from 'react';

import { Check, TriangleAlert, X } from 'lucide-react';

import { cn } from '@/shadcn/lib/utils';

export type ToastVariant = 'success' | 'error' | 'warning';

const surfaceStyles: Record<ToastVariant, string> = {
    success: 'border-success/30 bg-success/10',
    error: 'border-destructive/30 bg-destructive/10',
    warning: 'border-warning/30 bg-warning/10',
};

const badgeStyles: Record<ToastVariant, string> = {
    success: 'bg-success text-success-foreground',
    error: 'bg-destructive text-destructive-foreground',
    warning: 'bg-warning text-warning-foreground',
};

const badgeIcons: Record<ToastVariant, ReactNode> = {
    success: <Check className="size-4" />,
    error: <TriangleAlert className="size-3.5" />,
    warning: <span className="text-sm font-bold leading-none">!</span>,
};

type ToastMsgProps = {
    variant: ToastVariant;
    title?: string;
};

export const ToastMsg: FC<PropsWithChildren<ToastMsgProps>> = ({ variant, title, children }) => (
    <div
        className={cn(
            'flex w-full items-start gap-3 rounded-lg border px-4 py-3.5 pr-10',
            surfaceStyles[variant]
        )}
    >
        <span
            className={cn(
                'mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full',
                badgeStyles[variant]
            )}
        >
            {badgeIcons[variant]}
        </span>

        <div className="flex min-w-0 flex-col gap-0.5">
            {title ? <span className="text-base font-bold leading-6">{title}</span> : null}
            <div className="text-sm leading-5 wrap-break-word">{children}</div>
        </div>
    </div>
);

export const ToastCloseButton: FC<{ closeToast?: () => void }> = ({ closeToast }) => (
    <button
        type="button"
        aria-label="Close"
        onClick={closeToast}
        className="absolute top-2 right-2 rounded-md bg-transparent p-1 text-muted-foreground transition-colors hover:text-foreground"
    >
        <X className="size-4.5" />
    </button>
);
